import { EditorCore, GetDOMSelection } from "roosterjs";

const firefoxNodeContainsError =
    /^Node\.contains: Argument 1 does not implement interface Node\.?$/;

const isNodeContainsTypeError = (error: unknown): error is TypeError =>
    error instanceof TypeError && firefoxNodeContainsError.test(error.message);

const isInclusiveDescendant = (root: Node, node: Node): boolean => {
    // `instanceof Node` is realm-sensitive. Firefox also deliberately keeps a
    // node's original realm when it is adopted into another document.
    let current: Node | null = node;

    while (current) {
        if (current === root) {
            return true;
        }

        current = current.parentNode;
    }

    return false;
};

const getLiveDOMSelection = (core: EditorCore) => {
    const range = core.domHelper.getSelectionRange();

    if (
        !range ||
        !isInclusiveDescendant(core.logicalRoot, range.commonAncestorContainer)
    ) {
        return null;
    }

    const selection = core.logicalRoot.ownerDocument.defaultView?.getSelection();

    return {
        type: "range" as const,
        range,
        isReverted: selection
            ? selection.focusNode !== range.endContainer ||
              selection.focusOffset !== range.endOffset
            : false,
    };
};

/**
 * Work around Firefox rejecting a Range container in Node.contains().
 *
 * This uses RoosterJS's supported core API override and defers to the original
 * implementation unless Firefox throws its specific WebIDL conversion error.
 */
export const getDOMSelection: GetDOMSelection = core => {
    try {
        return core.originalApi.getDOMSelection(core);
    } catch (error) {
        if (!isNodeContainsTypeError(error)) {
            throw error;
        }

        return getLiveDOMSelection(core);
    }
};
